import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Trailer, ThemeClasses } from "../types";
import { getStatusColor, getStatusLabel } from "../utils";
import { useVehiclesRealtimeStore } from "../../../../../../store/admin/vehiclesRealtimeStore";
import { getTimeUntil } from "../../devices";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import {
  DENSE_ROW,
  ICON_BTN,
  ICON_BTN_DANGER,
  ICON_BTN_ON,
  STATUS_CHIP,
} from "../../../../shared";
interface TrailerListRowProps {
  trailer: Trailer;
  themeClasses: ThemeClasses;
  onEdit: (trailer: Trailer) => void;
  onDetails: (trailer: Trailer) => void;
  onDeviceClick?: (trailer: Trailer) => void;
  onDelete?: (trailer: Trailer) => void;
}

export function TrailerListRow({
  trailer,
  themeClasses,
  onEdit,
  onDetails,
  onDeviceClick,
  onDelete,
}: TrailerListRowProps) {
  const { t } = useTranslation("adminSections");
  const navigate = useNavigate();
  const setFocusedVehicleId = useVehiclesRealtimeStore(
    (state) => state.setFocusedVehicleId,
  );
  const setHighlightedVehicleId = useVehiclesRealtimeStore(
    (state) => state.setHighlightedVehicleId,
  );
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();
  // Verificar si el remolque tiene un device conectado (directo o a través de vehicle)
  const hasDevice = !!trailer.device_id || !!trailer.device;
  // Verificar si el remolque tiene un vehículo asociado
  const hasVehicle = !!trailer.vehicle_id && trailer.vehicle_id !== "N/A";
  const plattform = import.meta.env.VITE_THEME;
  const handleViewOnMap = () => {
    if (!hasVehicle || !trailer.vehicle_id) return;
    setFocusedVehicleId(trailer.vehicle_id);
    setHighlightedVehicleId(trailer.vehicle_id, 5000);
    navigate("/map");
  };
  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`;
  const linkedLabel = hasDevice ? "Device" : "Camion asignado";
  const linkedValue = hasDevice
    ? trailer.device?.name || trailer.device?.identifier || "GPS"
    : trailer.vehicle?.name || "N/A";
  const nextInspection = trailer.nextInspectionDate?._seconds
    ? getTimeUntil(trailer.nextInspectionDate._seconds)
    : "N/A";
  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      {trailer.image ? (
        <img
          src={trailer.image}
          alt={trailer.name}
          className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 bg-primary-500/10">
          <i className="ri-truck-line text-base text-primary-500"></i>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={trailer.type}>
            {trailer.type}
          </h3>
          <span className={`${STATUS_CHIP} ${getStatusColor(trailer.status || "")}`}>
            {getStatusLabel(trailer.status || "", t)}
          </span>
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {trailer.brand} {trailer.model} {trailer.year} • {trailer.plate}
        </p>
      </div>
      <div className="hidden md:block w-40 shrink-0 min-w-0" title={`${linkedLabel}: ${linkedValue}`}>
        <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>{linkedLabel}</p>
        <p className={`text-sm ${themeClasses.text} truncate`}>{linkedValue}</p>
      </div>
      <div className="hidden lg:block w-32 shrink-0 min-w-0">
        <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>Próxima inspección</p>
        <p className={`text-sm ${themeClasses.text} truncate`}>{nextInspection}</p>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {!isMonitor && !isReadOnly && (
          <>
            <CRUDTooltip>
              <button
                className={neutralBtn}
                onClick={() => onEdit(trailer)}
                disabled={disableCRUD}
                aria-label={t("trailers.actions.edit", "Editar")}
                title={t("trailers.actions.edit", "Editar")}
              >
                <i className="ri-edit-line"></i>
              </button>
            </CRUDTooltip>
            {/* Botón conectar/desconectar Device */}
            {plattform === "central-gps" && (
              <CRUDTooltip>
                <button
                  onClick={() => onDeviceClick?.(trailer)}
                  disabled={disableCRUD}
                  className={hasDevice ? ICON_BTN_ON : neutralBtn}
                  aria-label={
                    hasDevice
                      ? t("trailers.device.disconnect")
                      : t("trailers.device.connect")
                  }
                  title={
                    hasDevice
                      ? t("trailers.device.disconnect")
                      : t("trailers.device.connect")
                  }
                >
                  <i className="ri-gps-line"></i>
                  {!hasDevice && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-primary-500 rounded-full" />
                  )}
                </button>
              </CRUDTooltip>
            )}
            <button
              className={neutralBtn}
              onClick={handleViewOnMap}
              disabled={!hasDevice}
              aria-label={hasDevice ? "Ver en mapa" : "Sin device conectado"}
              title={hasDevice ? "Ver en mapa" : "Sin device conectado"}
            >
              <i className="ri-map-pin-line"></i>
            </button>
          </>
        )}
        <button
          className={neutralBtn}
          onClick={() => onDetails(trailer)}
          aria-label={t("trailers.actions.details", "Detalles")}
          title={t("trailers.actions.details", "Detalles")}
        >
          <i className="ri-eye-line"></i>
        </button>
        {onDelete && !isMonitor && !isReadOnly && (
          <CRUDTooltip>
            <button
              className={ICON_BTN_DANGER}
              onClick={() => onDelete(trailer)}
              disabled={disableCRUD}
              aria-label={t("trailers.actions.delete", "Eliminar")}
              title={t("trailers.actions.delete", "Eliminar")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </CRUDTooltip>
        )}
      </div>
    </div>
  );
}
